import { useState, useRef } from 'react'
import * as Yup from 'yup'
import { useFormik } from 'formik'
import { KTSVG } from '../../_metronic/helpers'
import { downloadCsv } from './helper'
import { swlAlert } from './sweetAlert'
import { downloadReportByDate } from '../company-module/api'

const dateDownloadSchema = Yup.object().shape({
    form_date: Yup.string().trim().required('From date is required'),
    to_date: Yup.string().trim().required('To date is required').test(
        'is-after',
        'To date must be after From date',
        function (value) {
            const { form_date } = this.parent
            if (!form_date || !value) return true
            return new Date(value) >= new Date(form_date)
        }
    ),
})

/**
 * DateOptionDownload – download by date range with progress bar.
 * Inline form: From date, To date, Download button. No modal.
 */
const DateOptionDownload = ({
    downloadApi,
    project_key,
    className = 'btn btn-light-primary btn-sm fw-semibold px-4',
    buttonLabel = 'Download',
    defaultFileName = 'download',
}) => {
    const [progress, setProgress] = useState(0)
    const progressIntervalRef = useRef(null)

    const formik = useFormik({
        initialValues: { form_date: '', to_date: '' },
        validationSchema: dateDownloadSchema,
        onSubmit: async (values, { setSubmitting }) => {
            setProgress(0)
            if (progressIntervalRef.current) {
                clearInterval(progressIntervalRef.current)
            }
            let prog = 0
            progressIntervalRef.current = setInterval(() => {
                prog += 8
                if (prog >= 70) {
                    prog = 70
                    clearInterval(progressIntervalRef.current)
                }
                setProgress(prog)
            }, 100)

            try {
                const response = await downloadReportByDate(downloadApi, `project_key=${project_key}&from_date=${values.form_date}&to_date=${values.to_date}`)

                setProgress(100)
                if (response?.code === 0) {
                    swlAlert(response)
                } else {
                    const fileName = `${defaultFileName}_${values.form_date}_to_${values.to_date}.csv`
                    downloadCsv({
                        data: response,
                        fileName,
                        fileType: 'text/csv',
                    })
                }
            } catch (err) {
                setProgress(100)
                swlAlert({ code: 0, message: err?.message || 'Download failed.' })
                console.error('Download error:', err)
            } finally {
                if (progressIntervalRef.current) {
                    clearInterval(progressIntervalRef.current)
                }
                setSubmitting(false)
                setTimeout(() => setProgress(0), 600)
            }
        },
    })

    return (
        <div className="d-flex flex-wrap align-items-end gap-3">
            <form
                className="d-flex flex-wrap align-items-end gap-3"
                onSubmit={formik.handleSubmit}
                noValidate
            >
                <div>
                    <label className="form-label fw-semibold fs-7 mb-1">From date</label>
                    <input
                        type="date"
                        className={`form-control form-control-solid form-control-sm ${formik.touched.form_date && formik.errors.form_date ? 'is-invalid' : ''}`}
                        style={{ minWidth: 140 }}
                        {...formik.getFieldProps('form_date')}
                    />
                    {formik.touched.form_date && formik.errors.form_date && (
                        <div className="invalid-feedback d-block small">{formik.errors.form_date}</div>
                    )}
                </div>
                <div>
                    <label className="form-label fw-semibold fs-7 mb-1">To date</label>
                    <input
                        type="date"
                        className={`form-control form-control-solid form-control-sm ${formik.touched.to_date && formik.errors.to_date ? 'is-invalid' : ''}`}
                        style={{ minWidth: 140 }}
                        {...formik.getFieldProps('to_date')}
                    />
                    {formik.touched.to_date && formik.errors.to_date && (
                        <div className="invalid-feedback d-block small">{formik.errors.to_date}</div>
                    )}
                </div>
                <button
                    type="submit"
                    disabled={formik.isSubmitting}
                    className={className}
                >
                    {formik.isSubmitting ? (
                        <>
                            <span className="spinner-border spinner-border-sm align-middle me-1" />
                            Wait...
                        </>
                    ) : (
                        <>
                            <KTSVG path="/media/icons/duotune/general/gen033.svg" className="svg-icon-4 me-1" />
                            {buttonLabel}
                        </>
                    )}
                </button>
            </form>

            {(formik.isSubmitting || progress > 0) && (
                <div className="d-flex align-items-center gap-3" style={{ minWidth: 180 }}>
                    <div className="progress flex-grow-1 h-6px" style={{ minWidth: 100 }}>
                        <div
                            className="progress-bar progress-bar-striped progress-bar-animated"
                            role="progressbar"
                            style={{
                                width: `${progress}%`,
                                transition: 'width 0.2s ease',
                                backgroundColor:
                                    progress < 50 ? '#f1416c' : progress < 90 ? '#ffc700' : '#50cd89',
                            }}
                            aria-valuenow={progress}
                            aria-valuemin="0"
                            aria-valuemax="100"
                        />
                    </div>
                    <span className="text-gray-700 fw-semibold fs-7">{progress}%</span>
                </div>
            )}
        </div>
    )
}

export { DateOptionDownload }
